import React from 'react';
import PropTypes from 'prop-types';
import invariant from 'invariant';
import dedent from 'dedent';
import Markdown from 'react-markdown';
import Typography from '@material-ui/core/Typography';

const headingLevelsToVariants = {
  1: 'h5',
  2: 'h6',
  3: 'subtitle1',
  default: 'subtitle1',
};

const HeadingRenderer = ({ level, children }) => (
  <Typography
    variant={headingLevelsToVariants[level] || headingLevelsToVariants.default}
  >
    {children}
  </Typography>
);

HeadingRenderer.propTypes = {
  level: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const extractKey = (keyString, value) =>
  keyString
    .split('.')
    .reduce((acc, key) => (acc && acc[key] != null ? acc[key] : null), value);

export const evaluateString = ({ dict, t, params }) => {
  const value = extractKey(t, dict);
  invariant(
    typeof value !== 'undefined' && value !== null,
    `language key ${t} doesn't exist`
  );
  if (typeof value === 'function') {
    return value(params);
  }
  invariant(
    params === null || params === undefined,
    `${t} doesn't support params`
  );
  return value;
};

export const markdown = (strs, ...args) => {
  const str = dedent(strs, ...args);
  return <Markdown components={{ heading: HeadingRenderer }} children={str} />;
};

export const LocaleContext = React.createContext({
  locale: null,
});

export default class T extends React.Component {
  static propTypes = {
    t: PropTypes.string.isRequired,
    params: PropTypes.shape({}),
    children: PropTypes.func,
    component: PropTypes.func,
  };

  static defaultProps = {
    params: null,
    children: null,
    component: null,
  };

  render() {
    const { t, params, children, component: Component, ...props } = this.props;

    if (process.env.REACT_APP_ENV === 'development' && !t) {
      return <h1 style={{ color: 'red' }}>KEY IS MISSING</h1>;
    }

    return (
      <LocaleContext.Consumer>
        {(dict) => {
          const value = evaluateString({ dict, t, params });
          if (children) {
            return children(value);
          }
          if (Component) {
            return <Component {...props}>{value}</Component>;
          }
          return value;
        }}
      </LocaleContext.Consumer>
    );
  }
}

export const withLocale = (Component) => (props) => (
  <LocaleContext.Consumer>
    {(locale) => <Component locale={locale} {...props} />}
  </LocaleContext.Consumer>
);

export const useLocale = () => {
  const dict = React.useContext(LocaleContext);
  return React.useCallback(
    (path, params = null) => evaluateString({ dict, t: path, params }),
    [dict]
  );
};
